import React, { useState, useEffect, useCallback } from 'react';
import { Radio, Power, Pause, Plus, Trash2, Settings, BarChart3, AlertTriangle, ChevronDown, ChevronUp, Shield, Zap, Eye, DollarSign, Activity } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE as API } from '../../lib/apiBase';


export default function AdCommandCenter() {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [showAddCampaign, setShowAddCampaign] = useState(false);
  const [newProvider, setNewProvider] = useState({ name: '', type: 'generic', enabled: false });
  const [newCampaign, setNewCampaign] = useState({ name: '', format: 'audio_30s', placement: 'pre_song', status: 'draft' });
  const [expandedSection, setExpandedSection] = useState('overview');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const [cfg, st, prov, camp] = await Promise.all([
        fetch(`${API}/api/admin/ads/config`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/ads/stats`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/ads/providers`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/ads/campaigns`, { headers }).then(r => r.json()),
      ]);
      setConfig(cfg);
      setStats(st);
      setProviders(prov.providers || []);
      setCampaigns(camp.campaigns || []);
    } catch (e) {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSystem = async (enabled) => {
    await fetch(`${API}/api/admin/ads/toggle`, { method: 'POST', headers, body: JSON.stringify({ enabled }) });
    fetchAll();
  };

  const togglePause = async (paused) => {
    await fetch(`${API}/api/admin/ads/pause`, { method: 'POST', headers, body: JSON.stringify({ paused }) });
    fetchAll();
  };

  const updateConfig = async (updates) => {
    await fetch(`${API}/api/admin/ads/config`, { method: 'PUT', headers, body: JSON.stringify(updates) });
    fetchAll();
  };

  const addProvider = async () => {
    await fetch(`${API}/api/admin/ads/providers`, { method: 'POST', headers, body: JSON.stringify(newProvider) });
    setShowAddProvider(false);
    setNewProvider({ name: '', type: 'generic', enabled: false });
    fetchAll();
  };

  const toggleProvider = async (id, enabled) => {
    await fetch(`${API}/api/admin/ads/providers/${id}`, { method: 'PUT', headers, body: JSON.stringify({ enabled }) });
    fetchAll();
  };

  const deleteProvider = async (id) => {
    await fetch(`${API}/api/admin/ads/providers/${id}`, { method: 'DELETE', headers });
    fetchAll();
  };

  const addCampaign = async () => {
    await fetch(`${API}/api/admin/ads/campaigns`, { method: 'POST', headers, body: JSON.stringify(newCampaign) });
    setShowAddCampaign(false);
    setNewCampaign({ name: '', format: 'audio_30s', placement: 'pre_song', status: 'draft' });
    fetchAll();
  };

  const updateCampaignStatus = async (id, status) => {
    await fetch(`${API}/api/admin/ads/campaigns/${id}/status`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
    fetchAll();
  };

  if (loading) return <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" /></div>;

  const Section = ({ id, title, icon, children }) => (
    <div className="bg-pulse-surface border border-pulse-border rounded-xl overflow-hidden">
      <button onClick={() => setExpandedSection(expandedSection === id ? '' : id)} className="w-full flex items-center justify-between p-4 hover:bg-pulse-elevated/50 transition-colors">
        <span className="flex items-center gap-2 font-heading font-bold text-sm">{icon} {title}</span>
        {expandedSection === id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {expandedSection === id && <div className="px-4 pb-4 border-t border-pulse-border pt-3">{children}</div>}
    </div>
  );

  return (
    <div data-testid="ad-command-center" className="space-y-4">
      {/* Master Controls */}
      <div className="flex flex-wrap gap-3 items-center bg-pulse-surface border border-pulse-border rounded-xl p-4">
        <div className="flex items-center gap-2">
          <Radio size={18} className="text-pulse-primary" />
          <span className="font-heading font-bold text-sm">Ad System</span>
        </div>
        <div className="flex gap-2 ml-auto">
          <button data-testid="ads-toggle" onClick={() => toggleSystem(!config?.enabled)} className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${config?.enabled ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
            <Power size={14} /> {config?.enabled ? 'ON' : 'OFF'}
          </button>
          {config?.enabled && (
            <button data-testid="ads-pause" onClick={() => togglePause(!config?.paused)} className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${config?.paused ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-pulse-elevated border border-pulse-border text-pulse-muted'}`}>
              <Pause size={14} /> {config?.paused ? 'PAUSED' : 'Pausar'}
            </button>
          )}
        </div>
      </div>

      {/* Overview Stats */}
      <Section id="overview" title="Rendimiento" icon={<BarChart3 size={16} className="text-cyan-400" />}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MiniStat label="Proveedores" value={`${stats?.active_providers || 0}/${stats?.total_providers || 0}`} color="cyan" />
          <MiniStat label="Campanas" value={`${stats?.active_campaigns || 0}/${stats?.total_campaigns || 0}`} color="green" />
          <MiniStat label="Impresiones" value={stats?.total_impressions?.toLocaleString() || '0'} color="purple" />
          <MiniStat label="Ingresos EUR" value={`${stats?.total_revenue_eur?.toFixed(2) || '0.00'}EUR`} color="amber" />
        </div>
        <div className="bg-pulse-elevated rounded-lg p-3">
          <p className="text-xs font-semibold mb-2">Reparto de ingresos</p>
          <div className="flex gap-4 text-xs">
            <span>Plataforma: <span className="text-cyan-400 font-bold">{((stats?.revenue_share?.platform || 0.6) * 100).toFixed(0)}%</span></span>
            <span>Pool Artistas: <span className="text-green-400 font-bold">{((stats?.revenue_share?.artist_pool || 0.25) * 100).toFixed(0)}%</span></span>
            <span>Oyente: <span className="text-amber-400 font-bold">{((stats?.revenue_share?.listener_reward || 0.15) * 100).toFixed(0)}%</span></span>
          </div>
        </div>
        {stats?.health?.alerts?.length > 0 && (
          <div className="mt-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-400 flex items-center gap-1"><AlertTriangle size={14} /> Alertas</p>
            {stats.health.alerts.map((a, i) => <p key={i} className="text-xs text-red-400/80 mt-1">{a}</p>)}
          </div>
        )}
      </Section>

      {/* Frequency & Rules */}
      <Section id="rules" title="Frecuencia y Reglas" icon={<Settings size={16} className="text-amber-400" />}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-xs text-pulse-muted">Canciones entre anuncios</label>
            <select data-testid="ad-frequency" value={config?.frequency_songs_between_ads || 4} onChange={e => updateConfig({ frequency_songs_between_ads: parseInt(e.target.value) })} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-1.5 text-sm">
              {[2, 3, 4, 5, 6, 8, 10].map(n => <option key={n} value={n}>{n} canciones</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-xs text-pulse-muted">Max anuncios/hora</label>
            <select value={config?.max_ads_per_hour || 6} onChange={e => updateConfig({ max_ads_per_hour: parseInt(e.target.value) })} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-1.5 text-sm">
              {[2, 4, 6, 8, 10, 15].map(n => <option key={n} value={n}>{n}/hora</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-xs text-pulse-muted">Suscriptores saltan anuncios</label>
            <button onClick={() => updateConfig({ subscriber_skip: !config?.subscriber_skip })} className={`px-3 py-1 rounded-lg text-xs font-bold ${config?.subscriber_skip ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {config?.subscriber_skip ? 'Si' : 'No'}
            </button>
          </div>
          <div>
            <p className="text-xs text-pulse-muted mb-2">Ubicaciones permitidas</p>
            <div className="flex flex-wrap gap-2">
              {['pre_song', 'mid_feed', 'banner_feed', 'sidebar'].map(p => (
                <span key={p} className={`px-2 py-1 rounded text-[10px] ${(config?.allowed_placements || []).includes(p) ? 'bg-cyan-500/20 text-cyan-400' : 'bg-pulse-elevated text-pulse-muted'}`}>{p}</span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs text-pulse-muted mb-2">Formatos permitidos</p>
            <div className="flex flex-wrap gap-2">
              {['audio_30s', 'audio_15s', 'banner', 'interstitial'].map(f => (
                <span key={f} className={`px-2 py-1 rounded text-[10px] ${(config?.allowed_formats || []).includes(f) ? 'bg-amber-500/20 text-amber-400' : 'bg-pulse-elevated text-pulse-muted'}`}>{f}</span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Providers */}
      <Section id="providers" title="Proveedores" icon={<Zap size={16} className="text-green-400" />}>
        <div className="space-y-2 mb-3">
          {providers.length === 0 ? (
            <p className="text-xs text-pulse-muted text-center py-4">Sin proveedores configurados</p>
          ) : providers.map(p => (
            <div key={p.id} data-testid={`provider-${p.id}`} className="flex items-center justify-between p-3 bg-pulse-elevated rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${p.enabled ? 'bg-green-400' : 'bg-red-400'}`} />
                <div>
                  <p className="text-sm font-semibold">{p.name}</p>
                  <p className="text-[10px] text-pulse-muted">{p.type} | CPM: {p.avg_cpm}EUR | Fill: {p.fill_rate}%</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggleProvider(p.id, !p.enabled)} className={`px-2 py-1 rounded text-[10px] font-bold ${p.enabled ? 'bg-green-500/20 text-green-400' : 'bg-pulse-surface text-pulse-muted'}`}>
                  {p.enabled ? 'ON' : 'OFF'}
                </button>
                <button onClick={() => deleteProvider(p.id)} className="px-2 py-1 rounded text-[10px] text-red-400 hover:bg-red-500/10"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
        <button data-testid="add-provider-btn" onClick={() => setShowAddProvider(!showAddProvider)} className="flex items-center gap-1 text-xs text-pulse-primary"><Plus size={14} /> Anadir proveedor</button>
        {showAddProvider && (
          <div className="mt-2 p-3 bg-pulse-elevated rounded-lg space-y-2">
            <input value={newProvider.name} onChange={e => setNewProvider({ ...newProvider, name: e.target.value })} placeholder="Nombre (ej: Google AdMob)" className="w-full bg-pulse-surface border border-pulse-border rounded px-3 py-1.5 text-sm" />
            <select value={newProvider.type} onChange={e => setNewProvider({ ...newProvider, type: e.target.value })} className="w-full bg-pulse-surface border border-pulse-border rounded px-3 py-1.5 text-sm">
              <option value="generic">Generico</option>
              <option value="programmatic">Programatico</option>
              <option value="direct">Venta directa</option>
              <option value="self-serve">Autoservicio</option>
            </select>
            <button onClick={addProvider} disabled={!newProvider.name} className="w-full bg-pulse-primary text-black py-2 rounded-lg text-sm font-bold disabled:opacity-50">Guardar</button>
          </div>
        )}
      </Section>

      {/* Campaigns */}
      <Section id="campaigns" title="Campanas" icon={<Eye size={16} className="text-purple-400" />}>
        <div className="space-y-2 mb-3">
          {campaigns.length === 0 ? (
            <p className="text-xs text-pulse-muted text-center py-4">Sin campanas</p>
          ) : campaigns.map(c => (
            <div key={c.id} data-testid={`campaign-${c.id}`} className="flex items-center justify-between p-3 bg-pulse-elevated rounded-lg">
              <div>
                <p className="text-sm font-semibold">{c.name}</p>
                <p className="text-[10px] text-pulse-muted">{c.format} | {c.placement} | {c.impressions} imp</p>
              </div>
              <div className="flex gap-1">
                {['draft', 'active', 'paused', 'completed'].map(s => (
                  <button key={s} onClick={() => updateCampaignStatus(c.id, s)} className={`px-2 py-1 rounded text-[10px] ${c.status === s ? 'bg-pulse-primary text-black font-bold' : 'text-pulse-muted hover:text-white'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button data-testid="add-campaign-btn" onClick={() => setShowAddCampaign(!showAddCampaign)} className="flex items-center gap-1 text-xs text-pulse-primary"><Plus size={14} /> Nueva campana</button>
        {showAddCampaign && (
          <div className="mt-2 p-3 bg-pulse-elevated rounded-lg space-y-2">
            <input value={newCampaign.name} onChange={e => setNewCampaign({ ...newCampaign, name: e.target.value })} placeholder="Nombre de campana" className="w-full bg-pulse-surface border border-pulse-border rounded px-3 py-1.5 text-sm" />
            <select value={newCampaign.format} onChange={e => setNewCampaign({ ...newCampaign, format: e.target.value })} className="w-full bg-pulse-surface border border-pulse-border rounded px-3 py-1.5 text-sm">
              <option value="audio_30s">Audio 30s</option>
              <option value="audio_15s">Audio 15s</option>
              <option value="banner">Banner</option>
              <option value="interstitial">Interstitial</option>
            </select>
            <select value={newCampaign.placement} onChange={e => setNewCampaign({ ...newCampaign, placement: e.target.value })} className="w-full bg-pulse-surface border border-pulse-border rounded px-3 py-1.5 text-sm">
              <option value="pre_song">Pre-Song</option>
              <option value="mid_feed">Mid Feed</option>
              <option value="banner_feed">Banner Feed</option>
              <option value="sidebar">Sidebar</option>
            </select>
            <button onClick={addCampaign} disabled={!newCampaign.name} className="w-full bg-pulse-primary text-black py-2 rounded-lg text-sm font-bold disabled:opacity-50">Crear</button>
          </div>
        )}
      </Section>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  const colors = { cyan: 'text-cyan-400', green: 'text-green-400', purple: 'text-purple-400', amber: 'text-amber-400' };
  return (
    <div className="bg-pulse-elevated rounded-lg p-3 text-center">
      <p className="text-[10px] text-pulse-muted">{label}</p>
      <p className={`text-lg font-bold ${colors[color]}`}>{value}</p>
    </div>
  );
}
